import { companyRequest } from '@/company/api';
import { apiUrl } from '@/lib/shell';
import type {
  CurrentWorkingDraftRef,
  InterviewWorkingPayloadV1,
  SaveWorkingDraftInput,
  SaveWorkingDraftResult,
  SavedWorkingDraft,
} from './authoring-types';
import type { LibraryInput, LibraryItem } from '../../../src/artifacts/lifecycle/library.mjs';
import type { LibraryPage } from './view-models/library';
import type {
  FinishCandidateReference,
  FinishArtifactInput,
  FinishArtifactReceiptInput,
  ArtifactFinishService,
} from '../../../src/artifacts/authoring/finish.mjs';
import type { SavedArtifactRead } from '../../../src/artifacts/saved-payload.mjs';
import type { SavedArtifactView } from '../../../src/artifacts/saved-view-contract.mjs';
import type { ExportCommitRequest } from '../../../src/artifacts/distribution/export-adapter.mjs';
import type {
  ExportCommit,
  ExportManifest,
  ArtifactDownloads,
  DownloadInput,
} from '../../../src/artifacts/distribution/types.mjs';
import type { ExportSelection } from '../../../src/artifacts/export/index.mjs';
import type { ShareAccess } from '../types';
import type { sharingApi } from '../sharing-api';
import type { SharePanelData } from './view-models/panels';
import type { ExportPanelData } from './view-models/panels';
import type { ArtifactStarter, WorkingDraftService } from '../../../src/artifacts/root.mjs';
import type { StandaloneNativeInterviewServices } from '../../../src/artifacts/native-standalone-server.mjs';
type SourceRead = NonNullable<StandaloneNativeInterviewServices['sourceRead']>;
import type { ArtifactVersionHistory } from '../../../src/app-data/artifact-lifecycle/library.mjs';

const SIGNED_APP_PICTURE = /^\/api\/v1\/app-pictures\/[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/u;
const CACHED_APP_PICTURE =
  /^\/api\/v1\/company\/apps\/([^/?#]{1,720})\/cached-picture\/([^/?#]{1,720})\/([^/?#]{1,720})\/[a-f0-9]{64}\/[1-9][0-9]{0,15}\/1x\?purpose=(editor|viewer)$/u;
const root = '/api/v1/company/artifacts';
const draftPath = (draftId: string) => `${root}/drafts/${encodeURIComponent(draftId)}`;
const exportPath = (artifactId: string) => `${root}/${encodeURIComponent(artifactId)}/exports`;
// Existing saved display + export contracts; the actual C4 job is allocated on
// the server and retained for original-only recovery, never minted by the UI.
type SavedExportSelection = Omit<ExportCommitRequest, 'exportJobId'> &
  Pick<SavedArtifactView, 'artifactId' | 'revisionId' | 'number'> &
  Pick<ExportSelection, 'sheetId'>;
/** The draft exactly as the working-draft service returns it: payload and the registered starter
 *  it was started from, each null when unavailable (the server's own private and original validation). */
export type InterviewDraft = Awaited<ReturnType<WorkingDraftService['loadDraft']>>;
/** A saved work's picture path as an image URL: its signed saved picture, or the picture of its cached view. A cached
 *  picture shows only for THIS row's own artifact, version and revision, at an admitted generation with its matching
 *  picture digest; a pending or mismatched one, or anything that is not such a path, has none (the row's placeholder,
 *  never an older picture). An App has one picture, the same for everyone who can see it (served as viewer), so the
 *  row's own purpose (an owner's or editor's) does not have to match it. */
export function ownPictureUrl(
  owner: Readonly<{
    artifactId: string;
    versionId: string;
    revisionId: string;
    thumbnailRef?: string | null;
    purpose?: LibraryItem['purpose'];
  }>,
  picture: unknown,
): string | null {
  if (typeof picture !== 'string') return null;
  if (SIGNED_APP_PICTURE.test(picture)) return apiUrl(picture);
  const cached = CACHED_APP_PICTURE.exec(picture);
  if (!cached || typeof owner.thumbnailRef !== 'string') return null;
  let parts: string[];
  try {
    parts = cached.map((part) => decodeURIComponent(part));
  } catch {
    return null; // a malformed escape is not this row's picture
  }
  const [, artifactId, versionId, revisionId] = parts;
  return artifactId === owner.artifactId &&
    versionId === owner.versionId &&
    revisionId === owner.revisionId
    ? apiUrl(picture)
    : null;
}

const CACHED_RESULT_PICTURE =
  /^\/api\/v1\/company\/results\/([^/?#]{1,720})\/cached-picture\/[1-9][0-9]{0,15}\/[a-f0-9]{64}\/[1-9][0-9]{0,15}\/1x$/u;

/** A living Result's cached picture path as an image URL, only for THIS row's own Result with a picture digest; the
 *  server answers it only for the current revision and the reader's current access. Anything else has none. */
export function ownResultPictureUrl(
  owner: Readonly<{ resultId: string; thumbnailRef?: string | null }>,
  picture: unknown,
): string | null {
  if (typeof picture !== 'string' || typeof owner.thumbnailRef !== 'string') return null;
  const cached = CACHED_RESULT_PICTURE.exec(picture);
  if (!cached) return null;
  try {
    return decodeURIComponent(cached[1]) === owner.resultId ? apiUrl(picture) : null;
  } catch {
    return null; // a malformed escape is not this row's picture
  }
}

export const artifactApi = {
  readAudience: (
    input: Pick<
      SavedArtifactView,
      'artifactId' | 'versionId' | 'revisionId' | 'number' | 'purpose'
    >,
  ) => {
    const { artifactId, ...facts } = input;
    const query = new URLSearchParams(
      Object.entries(facts).map(([key, value]) => [key, String(value)]),
    );
    return companyRequest<
      typeof input &
        Pick<SharePanelData, 'members' | 'publication' | 'publishedNumber'> & {
          item: ShareAccess & { id: string };
        }
    >(`${root}/${encodeURIComponent(artifactId)}/sharing?${query}`);
  },
  applyAudience: (
    selection: Pick<
      SavedArtifactView,
      'artifactId' | 'versionId' | 'revisionId' | 'number' | 'purpose'
    >,
    input: Parameters<typeof sharingApi.save>[0],
  ) =>
    companyRequest<{ status: 'applied'; item: ShareAccess & { id: string } }>(
      `${root}/${encodeURIComponent(selection.artifactId)}/sharing`,
      'POST',
      {
        selection,
        change: { ...input, confirmed: true },
      },
    ),

  /** The kinds the server's own registry carries (id, schemaVersion, labelKey, capabilities, exportFormats). */
  listKinds: (fetchImpl?: typeof fetch) =>
    companyRequest<{
      kinds: readonly { id: string; schemaVersion: number; exportFormats: readonly string[] }[];
    }>(`${root}/kinds`, 'GET', undefined, fetchImpl),
  readExportOptions: (input: SavedExportSelection, locale: string) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(input))
      if (value != null) query.set(key, String(value));
    query.set('locale', locale);
    return companyRequest<ExportPanelData>(`${exportPath(input.artifactId)}?${query}`);
  },
  exportArtifact: (input: SavedExportSelection, locale: string) =>
    companyRequest<ExportCommit>(exportPath(input.artifactId), 'POST', { ...input, locale }),
  readExportManifest: (
    artifactId: string,
    input: Pick<SavedExportSelection, 'versionId' | 'manifestId' | 'purpose'>,
  ) =>
    companyRequest<ExportManifest>(
      `${exportPath(artifactId)}/${encodeURIComponent(input.manifestId)}?` +
        new URLSearchParams({ versionId: input.versionId, purpose: input.purpose }),
    ),
  lookupExportReceipt: (input: SavedExportSelection) =>
    companyRequest<ExportCommit | null>(`${exportPath(input.artifactId)}/receipt`, 'POST', input),
  createExportDownloadLease: (artifactId: string, input: DownloadInput) =>
    companyRequest<Awaited<ReturnType<ArtifactDownloads['createDownloadLease']>>>(
      `${exportPath(artifactId)}/downloads`,
      'POST',
      input,
    ),
  readDraftSource: (input: Parameters<SourceRead>[1]) =>
    companyRequest<Awaited<ReturnType<SourceRead>>>(
      `${draftPath(input.target.draft.draftId)}/source`,
      'POST',
      input,
    ),
  readHistory: (input: { artifactId: string; purpose: 'viewer' | 'editor' }) =>
    companyRequest<ArtifactVersionHistory>(
      `${root}/${encodeURIComponent(input.artifactId)}/versions?purpose=${encodeURIComponent(input.purpose)}`,
    ),
  prepareFinish: (input: FinishCandidateReference, fetchImpl?: typeof fetch) =>
    companyRequest<FinishArtifactInput>(
      `${root}/${encodeURIComponent(input.artifactId)}/finish/prepare`,
      'POST',
      input,
      fetchImpl,
    ),
  finish: (input: FinishArtifactInput, fetchImpl?: typeof fetch) =>
    companyRequest<Awaited<ReturnType<ArtifactFinishService['finish']>>>(
      `${root}/${encodeURIComponent(input.artifactId)}/finish`,
      'POST',
      input,
      fetchImpl,
    ),
  lookupFinishReceipt: (input: FinishArtifactReceiptInput, fetchImpl?: typeof fetch) =>
    companyRequest<Awaited<ReturnType<ArtifactFinishService['lookupReceipt']>>>(
      `${root}/${encodeURIComponent(input.artifactId)}/finish/receipt`,
      'POST',
      input,
      fetchImpl,
    ),
  readVersion: (input: SavedArtifactRead, fetchImpl?: typeof fetch) =>
    companyRequest<SavedArtifactView>(
      `${root}/versions/${encodeURIComponent(input.versionId)}?purpose=${encodeURIComponent(input.purpose)}`,
      'GET',
      undefined,
      fetchImpl,
    ),
  library: (input: LibraryInput, fetchImpl?: typeof fetch) => {
    const query = new URLSearchParams();
    if (input.kind != null) throw new TypeError('Use the canonical group kinds');
    for (const kind of input.kinds ?? []) query.append('kinds', kind);
    if (input.segment) query.set('segment', input.segment);
    if (input.query !== undefined) query.set('query', input.query);
    if (input.cursor !== undefined) query.set('cursor', input.cursor);
    if (input.limit !== undefined) query.set('limit', String(input.limit));
    return companyRequest<LibraryPage>(`${root}/library?${query}`, 'GET', undefined, fetchImpl);
  },
  /** The path the Library gave an App row for its own picture, as an image URL (ownPictureUrl). Another kind has none. */
  appPictureUrl: (item: Readonly<LibraryItem> & { picture?: unknown }): string | null =>
    item.kind === 'app' ? ownPictureUrl(item, item.picture) : null,
  loadDraft: (draftId: string) => companyRequest<InterviewDraft>(draftPath(draftId)),
  prepareSave: (
    input: {
      draftId: string;
      expectedDraftGeneration: number;
      operationId: string;
      payload: InterviewWorkingPayloadV1;
    },
    fetchImpl?: typeof fetch,
  ) =>
    companyRequest<SaveWorkingDraftInput>(
      `${draftPath(input.draftId)}/stage`,
      'POST',
      input,
      fetchImpl,
    ),
  saveWorkingDraft: (input: SaveWorkingDraftInput, fetchImpl?: typeof fetch) =>
    companyRequest<SaveWorkingDraftResult>(
      `${draftPath(input.draftId)}/save`,
      'POST',
      input,
      fetchImpl,
    ),
  lookupSaveReceipt: (input: SaveWorkingDraftInput, fetchImpl?: typeof fetch) =>
    companyRequest<
      | { status: 'committed'; value: SavedWorkingDraft }
      | { status: 'absent' | 'unresolved' }
      | { status: 'conflict'; code: 'operation_digest_mismatch' }
    >(`${draftPath(input.draftId)}/receipt`, 'POST', input, fetchImpl),
};

/** Bind the common audience chip to one genuine saved occurrence. No Save or
 * publication method is exposed; apply consumes the last server-read CAS. */
export function artifactAudienceWriter(
  saved: Required<
    Pick<
      import('../library/save-to-library').SavedItem,
      'artifactId' | 'versionId' | 'revisionId' | 'versionNumber' | 'purpose'
    >
  >,
) {
  const selection = Object.freeze({
    artifactId: saved.artifactId,
    versionId: saved.versionId,
    revisionId: saved.revisionId,
    number: saved.versionNumber,
    purpose: saved.purpose,
  });
  let current: ReturnType<typeof audienceState> | null = null;
  return {
    async read() {
      const response = await artifactApi.readAudience(selection);
      for (const key of ['artifactId', 'versionId', 'revisionId', 'number', 'purpose'] as const)
        if (response[key] !== selection[key]) throw new Error('Sharing target changed.');
      current = audienceState(selection.artifactId, response.item);
      return current;
    },
    async apply(change: Pick<Parameters<typeof sharingApi.save>[0], 'visibility' | 'audience'>) {
      if (!current?.canShare) throw new Error('Sharing permission unavailable.');
      const response = await artifactApi.applyAudience(selection, {
        kind: 'result',
        id: selection.artifactId,
        revision: current.sharingRevision,
        ...change,
      });
      if (response.status !== 'applied') throw new Error('Sharing response unavailable.');
      current = audienceState(selection.artifactId, response.item);
      return current;
    },
  };
}

function audienceState(artifactId: string, item: ShareAccess & { id: string }) {
  if (
    item.id !== artifactId ||
    typeof item.sharingRevision !== 'number' ||
    !Number.isSafeInteger(item.sharingRevision) ||
    item.sharingRevision < 1
  )
    throw new Error('Sharing response unavailable.');
  if (
    item.visibility !== 'private' &&
    item.visibility !== 'members' &&
    item.visibility !== 'company'
  )
    throw new Error('Sharing response unavailable.');
  return {
    ...item,
    visibility: item.visibility,
    sharingRevision: item.sharingRevision,
    canShare: item.canShare === true,
  };
}
